import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  CarHistoryTimelineProps,
  CarHistoryEvent,
} from './CarHistoryTimeline.types';
// import { TimelineItem } from './TimelineItem';
import { TimelineItemHSP } from './TimelineItemHSP';
import { APP_COLORS } from '@/config/colors';
import { parse, parseISO, isValid } from 'date-fns';

/**
 * Parsuje datę w różnych formatach i zwraca obiekt Date używając date-fns
 */
const parseDate = (dateStr: string): Date => {
  if (!dateStr) {
    return new Date();
  }

  // Format DD.MM.YYYY (np. '30.07.2025')
  if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}/)) {
    try {
      const parsed = parse(dateStr, 'dd.MM.yyyy', new Date());
      if (isValid(parsed)) {
        return parsed;
      }
    } catch {
      // Ignoruj błędy parsowania
    }
  }

  // Format YYYY-MM-DD (np. '2025-07-30')
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    try {
      const parsed = parseISO(dateStr);
      if (isValid(parsed)) {
        return parsed;
      }
    } catch {
      // Ignoruj błędy parsowania
    }
  }

  // Próbujemy parsować jako ISO (parseISO obsługuje wiele formatów ISO)
  try {
    const parsed = parseISO(dateStr);
    if (isValid(parsed)) {
      return parsed;
    }
  } catch {
    // Ignoruj błędy parsowania
  }

  // Fallback - zwracamy aktualną datę
  return new Date();
};

/**
 * Wyodrębnia rok z daty w różnych formatach (YYYY-MM-DD, DD.MM.YYYY, itp.)
 */
const extractYear = (date: string): number => {
  const parsedDate = parseDate(date);
  return parsedDate.getFullYear();
};

/**
 * Tworzy wydarzenie "Brak danych" dla danego roku
 */
const createNoDataEvent = (year: number, index: number): CarHistoryEvent => {
  return {
    id: `no-data-${year}-${index}`,
    date: `${year}-01-01`,
    type: 'no_data',
    title: 'Brak danych',
    description: 'Brak informacji o zdarzeniach w tym okresie',
  };
};

/**
 * Wykrywa luki w danych i zwraca listę wydarzeń "Brak danych" do wstawienia
 */
const detectDataGaps = (
  events: CarHistoryEvent[],
  currentYear: number,
): CarHistoryEvent[] => {
  // Filtrujemy tylko wydarzenia, które nie są już "Brak danych"
  const realEvents = events.filter((e) => e.type !== 'no_data');

  if (realEvents.length === 0) {
    return [];
  }

  // Znajdź wszystkie lata z wydarzeń
  const yearsWithData = new Set<number>();
  realEvents.forEach((event) => {
    const year = extractYear(event.date);
    yearsWithData.add(year);
  });

  // Posortuj lata z danymi
  const sortedYears = Array.from(yearsWithData).sort((a, b) => a - b);
  const lastYearWithData = sortedYears[sortedYears.length - 1];

  const noDataEvents: CarHistoryEvent[] = [];
  let noDataIndex = 0;

  // Sprawdź luki między kolejnymi latami z danymi
  // Jeśli różnica między latami > 1, dodaj "Brak danych" dla lat między nimi
  for (let i = 0; i < sortedYears.length - 1; i++) {
    const currentYearWithData = sortedYears[i];
    const nextYearWithData = sortedYears[i + 1];

    // Jeśli między latami jest luka (różnica > 1), dodaj "Brak danych"
    if (nextYearWithData - currentYearWithData > 1) {
      for (
        let gapYear = currentYearWithData + 1;
        gapYear < nextYearWithData;
        gapYear++
      ) {
        noDataEvents.push(createNoDataEvent(gapYear, noDataIndex++));
      }
    }
  }

  // Dodaj "Brak danych" dla lat od ostatniego roku z danymi + 1 do aktualnego roku
  if (lastYearWithData < currentYear) {
    for (let year = lastYearWithData + 1; year <= currentYear; year++) {
      noDataEvents.push(createNoDataEvent(year, noDataIndex++));
    }
  }

  return noDataEvents;
};

/**
 * Konsoliduje kolejne wydarzenia "Brak danych" w jedno wydarzenie z zakresem lat
 * Jeśli jest minimum 2 kolejne lata "Brak danych", grupuje je w jeden item
 */
const consolidateNoDataEvents = (
  events: CarHistoryEvent[],
): CarHistoryEvent[] => {
  const result: CarHistoryEvent[] = [];
  let currentNoDataGroup: CarHistoryEvent[] = [];

  events.forEach((event, index) => {
    if (event.type === 'no_data') {
      // Dodajemy do aktualnej grupy "Brak danych"
      currentNoDataGroup.push(event);

      // Jeśli to ostatnie wydarzenie, przetwarzamy zgromadzoną grupę
      if (index === events.length - 1) {
        if (currentNoDataGroup.length >= 2) {
          const years = currentNoDataGroup
            .map((e) => extractYear(e.date))
            .sort((a, b) => a - b);
          const startYear = years[0];
          const endYear = years[years.length - 1];

          const consolidatedEvent: CarHistoryEvent = {
            id: `no-data-range-${startYear}-${endYear}`,
            date: `${startYear}-01-01`,
            type: 'no_data',
            title: 'Brak danych',
            description: `Brak informacji o zdarzeniach w okresie ${startYear}-${endYear}`,
            yearRange: `${startYear}-${endYear}`,
          };

          result.push(consolidatedEvent);
        } else {
          result.push(currentNoDataGroup[0]);
        }
      }
    } else {
      // Napotkaliśmy wydarzenie inne niż "Brak danych"
      // Przetwarzamy zgromadzoną grupę
      if (currentNoDataGroup.length >= 2) {
        // Konsolidujemy minimum 2 kolejne lata w jeden item
        const years = currentNoDataGroup
          .map((e) => extractYear(e.date))
          .sort((a, b) => a - b);
        const startYear = years[0];
        const endYear = years[years.length - 1];

        const consolidatedEvent: CarHistoryEvent = {
          id: `no-data-range-${startYear}-${endYear}`,
          date: `${startYear}-01-01`,
          type: 'no_data',
          title: 'Brak danych',
          description: `Brak informacji o zdarzeniach w okresie ${startYear}-${endYear}`,
          yearRange: `${startYear}-${endYear}`,
        };

        result.push(consolidatedEvent);
      } else if (currentNoDataGroup.length === 1) {
        // Jeśli jest tylko 1 rok, dodajemy go bez zmian
        result.push(currentNoDataGroup[0]);
      }

      // Resetujemy grupę i dodajemy bieżące wydarzenie
      currentNoDataGroup = [];
      result.push(event);
    }
  });

  return result;
};

/**
 * Komponent do wyświetlania markera roku
 */
const YearMarkerItem: React.FC<{ year: number }> = ({ year }) => {
  return (
    <div className="flex relative z-10 items-start mb-8 md:items-center">
      <div className="flex flex-col items-center mr-6 md:w-48">
        <div className="px-6 py-1 text-sm font-semibold tracking-wide text-white uppercase rounded-full bg-text-primary">
          {year}
        </div>
      </div>
      <div className="relative flex-1 items-center">
        {/* Pusty element dla wyrównania */}
      </div>
    </div>
  );
};

/**
 * Komponent do rysowania segmentów linii timeline
 */
const TimelineLineSegments: React.FC<{
  items: Array<{ type: 'event' | 'year'; event?: CarHistoryEvent }>;
  itemRefs: React.RefObject<HTMLDivElement>[];
  containerRef: React.RefObject<HTMLDivElement>;
}> = ({ items, itemRefs, containerRef }) => {
  const [segments, setSegments] = useState<
    Array<{
      top: number;
      height: number;
      isDashed: boolean;
    }>
  >([]);

  useEffect(() => {
    const calculateSegments = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newSegments: Array<{
        top: number;
        height: number;
        isDashed: boolean;
      }> = [];

      // On mobile (< md breakpoint), add initial line from top of first item to its center
      // On desktop the date pill is already at the item center so no extra segment is needed
      const isMobile = window.innerWidth < 768;
      if (isMobile && items.length > 0 && items[0].type !== 'year' && itemRefs[0]?.current) {
        const firstRect = itemRefs[0].current.getBoundingClientRect();
        const firstTop = firstRect.top - containerRect.top;
        const firstCenter = firstTop + firstRect.height / 2;
        if (firstCenter > firstTop) {
          newSegments.push({
            top: firstTop,
            height: firstCenter - firstTop,
            isDashed: false,
          });
        }
      }

      for (let i = 0; i < items.length - 1; i++) {
        const currentItem = items[i];
        const nextItem = items[i + 1];
        const currentRef = itemRefs[i];
        const nextRef = itemRefs[i + 1];

        // Sprawdzamy czy refs istnieją i mają current
        if (!currentRef || !nextRef || !currentRef.current || !nextRef.current) {
          continue;
        }

        const currentRect = currentRef.current.getBoundingClientRect();
        const nextRect = nextRef.current.getBoundingClientRect();

        // Obliczamy pozycje względem kontenera
        const currentTop = currentRect.top - containerRect.top;
        const nextTop = nextRect.top - containerRect.top;

        // Środek obecnego elementu
        const currentCenter = currentTop + currentRect.height / 2;
        // Środek następnego elementu
        const nextCenter = nextTop + nextRect.height / 2;

        // Sprawdzamy czy elementy to "Brak danych"
        const currentIsNoData = currentItem.event?.type === 'no_data';
        const nextIsNoData = nextItem.event?.type === 'no_data';

        // Linia jest przerywana jeśli:
        // - obecny element to "Brak danych" LUB
        // - następny element to "Brak danych" LUB
        // - oba elementy to "Brak danych"
        const isDashed = currentIsNoData || nextIsNoData;

        newSegments.push({
          top: currentCenter,
          height: nextCenter - currentCenter,
          isDashed,
        });
      }

      setSegments(newSegments);
    };

    calculateSegments();
    window.addEventListener('resize', calculateSegments);
    return () => window.removeEventListener('resize', calculateSegments);
  }, [items, itemRefs, containerRef]);

  return (
    <>
      {segments.map((segment, index) => (
        <div
          key={`segment-${index}`}
          className="absolute left-4 z-0 w-px md:left-24"
          style={{
            top: `${segment.top}px`,
            height: `${segment.height}px`,
            ...(segment.isDashed
              ? {
                backgroundImage: `repeating-linear-gradient(
                    to bottom,
                    ${APP_COLORS.grayBorder} 0px,
                    ${APP_COLORS.grayBorder} 4px,
                    transparent 4px,
                    transparent 8px
                  )`,
              }
              : {
                backgroundColor: APP_COLORS.grayBorder,
              }),
          }}
        />
      ))}
    </>
  );
};

/**
 * Komponent timeline do wyświetlania historii pojazdu
 * Pokazuje chronologię zdarzeń w życiu samochodu
 */
export const CarHistoryTimeline: React.FC<CarHistoryTimelineProps> = ({
  events,
  className = '',
  i18n,
  showNoDataGaps = true,
  showYearMarkers = true,
  renderItem,
}) => {
  // Tworzymy refs dla każdego elementu timeline (muszą być przed wczesnym return)
  const containerRef = useRef<HTMLDivElement>(null);

  // Obliczamy timelineItems używając useMemo
  const timelineItems = useMemo(() => {
    if (!events.length) {
      return [];
    }

    // Pobieramy aktualny rok
    const currentYear = new Date().getFullYear();

    // Wykrywamy luki w danych i dodajemy wydarzenia "Brak danych" (tylko jeśli włączone)
    const noDataEvents = showNoDataGaps ? detectDataGaps(events, currentYear) : [];

    // Łączymy oryginalne wydarzenia z automatycznie wykrytymi "Brak danych"
    const allEvents = [...events, ...noDataEvents];

    // Sortujemy wydarzenia chronologicznie
    const sortedEvents = [...allEvents].sort((a, b) => {
      const dateA = parseDate(a.date).getTime();
      const dateB = parseDate(b.date).getTime();
      return dateA - dateB;
    });

    // Konsolidujemy kolejne wydarzenia "Brak danych" w jeden item z zakresem lat (tylko jeśli włączone)
    const consolidatedEvents = showNoDataGaps ? consolidateNoDataEvents(sortedEvents) : sortedEvents;

    // Tworzymy listę elementów timeline z markerami roku
    type TimelineItem =
      | { type: 'event'; event: CarHistoryEvent; originalIndex: number }
      | { type: 'year'; year: number };

    // Grupujemy wydarzenia według roku, żeby sprawdzić czy w danym roku są tylko "Brak danych"
    const eventsByYear = new Map<number, CarHistoryEvent[]>();
    consolidatedEvents.forEach((event) => {
      const year = extractYear(event.date);
      if (!eventsByYear.has(year)) {
        eventsByYear.set(year, []);
      }
      eventsByYear.get(year)!.push(event);
    });

    const items: TimelineItem[] = [];
    let lastYear: number | null = null;

    consolidatedEvents.forEach((event) => {
      const eventYear = extractYear(event.date);
      // Dla automatycznie dodanych wydarzeń "Brak danych" używamy -1 jako originalIndex
      const originalIndex = events.findIndex((e) => e.id === event.id);

      // Sprawdzamy czy w danym roku są tylko wydarzenia "Brak danych"
      const yearEvents = eventsByYear.get(eventYear) || [];
      const hasOnlyNoData =
        yearEvents.length > 0 && yearEvents.every((e) => e.type === 'no_data');

      // Dodajemy marker roku przed pierwszym wydarzeniem każdego roku
      // Ale pomijamy marker jeśli w danym roku są tylko wydarzenia "Brak danych"
      if (showYearMarkers && (lastYear === null || lastYear !== eventYear)) {
        if (!hasOnlyNoData) {
          items.push({ type: 'year', year: eventYear });
        }
      }
      if (lastYear === null || lastYear !== eventYear) {
        lastYear = eventYear;
      }

      items.push({
        type: 'event',
        event,
        originalIndex: originalIndex >= 0 ? originalIndex : -1,
      });
    });

    return items;
  }, [events, showNoDataGaps, showYearMarkers]);

  // Inicjalizujemy refs synchronicznie dla każdego elementu
  const itemRefs = useMemo(
    () => timelineItems.map(() => React.createRef<HTMLDivElement>()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timelineItems.length],
  );

  if (!events.length) {
    return (
      <div className="flex justify-center items-center py-8 text-grayText">
        Brak danych o historii pojazdu
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <TimelineLineSegments
        items={timelineItems}
        itemRefs={itemRefs}
        containerRef={containerRef}
      />

      {timelineItems.map((item, index) => {
        if (item.type === 'year') {
          return (
            <div key={`year-${item.year}`} ref={itemRefs[index]}>
              <YearMarkerItem year={item.year} />
            </div>
          );
        }

        // Dla automatycznie dodanych wydarzeń "Brak danych" originalIndex będzie -1
        const isFirst = item.originalIndex === 0;
        const isLast =
          item.originalIndex >= 0 && item.originalIndex === events.length - 1;

        return (
          <div key={item.event.id} ref={itemRefs[index]}>
            {renderItem ? (
              renderItem({ event: item.event, isFirst, isLast })
            ) : (
              <TimelineItemHSP
                event={item.event}
                isFirst={isFirst}
                isLast={isLast}
                i18n={i18n}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
